<template>
  <div class="configurator">
    <card id="configurator" :hide-title="true" v-if="canEdit || canDelete">
      <div class="configurator-heading">
        <span class="fs24-700 configurator-title">{{ $t('import_configurator') }}</span>
        <div v-if="isSuperAdmin" class="configurator-actions">
          <SmagriaButton
            :width="300"
            customClass="primary"
            :disabled="checkingParcelSync || !parcelSyncAvailable || dispatchingParcelSync"
            :title="parcelSyncAvailable ? $t('tools.configurator.dispatch_parcel_data_sync') : $t('tools.configurator.parcel_data_sync_unavailable')"
            @click="showParcelSyncConfirmation = true"
          >
            <i class="dx-icon dx-icon-refresh" aria-hidden="true"></i>
            {{ $t('tools.configurator.dispatch_parcel_data_sync') }}
          </SmagriaButton>
          <SmagriaButton
            :width="300"
            customClass="primary"
            :disabled="dispatchingEmailImport"
            :label="$t('tools.configurator.dispatch_email_download')"
            @click="showEmailImportConfirmation = true"
          />
        </div>
      </div>
      <div class="row table-responsive">
        <div class="col-lg-12">
          <table class="table">
            <th class="text-left">{{ $t('import_type') }}</th>
            <th></th>
            <th v-if="isSuperAdmin"></th>
            <th></th>
            <tbody>
              <tr v-for="type in types" :key="Math.random()"
                @mouseover="hoveredRow = type.id" @mouseleave="hoveredRow = null">
                <td class="td-text text-left" :title="$t('import_type')" :class="{ 'selection-row': isHovered(type.id) }">
                  {{ $t('tools.configurator.' + lineToUnderscore(type.ref)) }}
                </td>
                <td class="td-text text-center" :class="{ 'selection-row': isHovered(type.id) }">
                  <img :src="require('@/assets/images/desktop/icons/controls/edit.svg')"
                    :class="['edit', 'success-filter']" :title="$t('tools.configurator.edit_templates')"
                    class="action-icon" @click="goToImportTemplatePage(type.id)" />
                </td>
                <td v-if="isSuperAdmin" class="td-text text-center settings-dialog-col" :class="{ 'selection-row': isHovered(type.id) }" >
                    <img v-bind:style="type.id === types.SGPS_DASC_IMPORT.id ? { marginLeft: '40px' } : {}" :src="require('@/assets/images/desktop/icons/controls/cog.svg')"
                    :class="['cog', 'primary-filter']" :title="$t('common.settings')" class="action-icon"
                    @click="showSettingsDialog(type)" />
                    <img style="margin-left: 20px;" v-if="type.id === types.SGPS_DASC_IMPORT.id" :src="require('@/assets/images/desktop/icons/controls/queue.svg')"
                    :class="['queue']" :title="$t('tools.statuses.edit_status')"
                    class="action-icon" @click="goToCriterialBuilderPage()" />
                </td>
                <td class="td-text text-center" :class="{ 'selection-row': isHovered(type.id) }">
                  <img :src="require('@/assets/images/desktop/icons/controls/arrow-down.svg')"
                    :class="['uploaded', 'secondary-filter']" :title="$t('import_history')" class="action-icon"
                    @click="goToImportHistoryPage(type)" />
                </td>
              </tr>
            </tbody>
          </table>
          <ImportSettingsDialog v-if="isSuperAdmin" :show="showImportSettingsModal" @close="showImportSettingsModal = false" :importTypes="types"
            :data="importSettingsModalData" @onOK="showImportSettingsModal = false"></ImportSettingsDialog>
        </div>
      </div>
      <ConfirmDxPopup
        v-if="isSuperAdmin"
        :show="showParcelSyncConfirmation"
        :title="$t('tools.configurator.parcel_data_sync_confirmation_title')"
        :message="$t('tools.configurator.parcel_data_sync_confirmation_message')"
        @close="showParcelSyncConfirmation = false"
        @confirm="dispatchParcelSync"
      />
      <ConfirmDxPopup
        v-if="isSuperAdmin"
        :show="showEmailImportConfirmation"
        :title="$t('tools.configurator.email_import_confirmation_title')"
        :message="$t('tools.configurator.email_import_confirmation_message')"
        @close="showEmailImportConfirmation = false"
        @confirm="dispatchEmailImport"
      />
    </card>
    <div v-else>
      <forbidden></forbidden>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { canDelete, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import { types } from "@/helpers/ImportTypes"
import { errorMessage, lineToUnderscore, successMessage } from "~/helpers/common";
import ImportSettingsDialog from './partials/ImportSettingsDialog.vue'
import ConfirmDxPopup from "@/components/Modals/ConfirmDxPopup.vue";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import axios from "@/plugins/axios";

export default {
  name: 'Configurator',
  data() {
    return {
      types: [],
      importSettingsModalData: {},
      showImportSettingsModal: false,
      hoveredRow: null,
      showEmailImportConfirmation: false,
      dispatchingEmailImport: false,
      parcelSyncAvailable: false,
      checkingParcelSync: true,
      dispatchingParcelSync: false,
      showParcelSyncConfirmation: false,
    }
  },
  components: {
    Forbidden,
    ImportSettingsDialog,
    ConfirmDxPopup,
    SmagriaButton,
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    isSuperAdmin() {
      return Boolean(this.user && this.user.is_superadmin === 1);
    },
    config() {
      return {
        permission: permissions.CONFIGURATOR,
        permission_type: permissionTypes.module,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    isHovered(id) {
      return this.hoveredRow === id;
    },
    goToImportTemplatePage(import_type_id) {
      this.$router.push({ path: '/tools/import-configurator/templates', query: { import_type_id: import_type_id } });
    },
    goToCriterialBuilderPage() {
      this.$router.push({ path: '/tools/statuses', query: { type: 'import_validated_data' } });
    },
    goToImportHistoryPage(import_type) {
      this.$router.push({ path: '/tools/import-history', query: { import_type_id: import_type.id, import_typ_ref: import_type.ref } });
    },
    showSettingsDialog(import_type) {
      this.importSettingsModalData.import_id = import_type.id;
      this.importSettingsModalData.import_ref = import_type.ref;
      this.importSettingsModalData.is_active_api_import = import_type.is_active_api_import;
      this.showImportSettingsModal = true;
    },
    lineToUnderscore(value) {
      return lineToUnderscore(value);
    },
    async loadParcelSyncStatus() {
      try {
        const response = await axios.get("/import-configurator/parcel-data-sync/status");
        this.parcelSyncAvailable = response.data.enabled === true;
      } catch (error) {
        this.parcelSyncAvailable = false;
      } finally {
        this.checkingParcelSync = false;
      }
    },
    async dispatchParcelSync() {
      this.showParcelSyncConfirmation = false;
      if (!this.parcelSyncAvailable || this.dispatchingParcelSync) return;
      this.dispatchingParcelSync = true;

      try {
        await axios.post("/import-configurator/parcel-data-sync/dispatch");
        successMessage(this, this.$t("tools.configurator.parcel_data_sync_dispatched"));
      } catch (error) {
        errorMessage(this, this.$t("tools.configurator.parcel_data_sync_dispatch_failed"));
      } finally {
        this.dispatchingParcelSync = false;
      }
    },
    async dispatchEmailImport() {
      this.showEmailImportConfirmation = false;
      this.dispatchingEmailImport = true;

      try {
        await axios.post("/import-configurator/email-import/dispatch");
        successMessage(this, this.$t("tools.configurator.email_import_dispatched"));
      } catch (error) {
        errorMessage(this, this.$t("tools.configurator.email_import_dispatch_failed"));
      } finally {
        this.dispatchingEmailImport = false;
      }
    },
  },
  mounted() {
    this.types = types;
    if (this.isSuperAdmin) {
      this.loadParcelSyncStatus();
    }
  },
}
</script>

<style scoped>
.action-icon {
  width: 22px;
  height: 22px;
  cursor: pointer;
}

.configurator-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.configurator-title {
  font-size: 32px;
  color: #3d3d3a;
}

.configurator-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

@media (max-width: 575px) {
  .configurator-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}

.selection-row {
  background-color: #e6e6e3;
}
</style>